import React from 'react';
import { Title, Loader, MedicalSupplySelectModal, MedicalSupplySelectModalHandle } from '../../elements';
import { extractApiErrorMessage, notifications } from '../../utils';
import SaveIcon from '@material-ui/icons/Save';
import AssessmentIcon from '@material-ui/icons/Assessment';
import { SettingsService } from '../../hooks/SettingsService';
import {
  Grid,
  Card,
  CardContent,
  Button,
  Typography,
  IconButton,
  Icon,
  Tooltip,
  Box,
  Divider,
  Switch
} from '@material-ui/core';
import { useStyles } from './styles';
import { MedicalSupplyViewModel } from '../../models/injection';
import { LoaderSize } from '../../constants/constants';
import { AnimalType } from '../../models/animal';
import { BarChartData, FarmSettingsOptions } from '../../models/settings';
import { useTranslation } from 'react-i18next';
import { FarmType } from '../../models/farm';
import { colors, getDashboardConfigByFarmType } from '../../utils';

const DashboardSettings: React.FC<any> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const settingsService = new SettingsService();

  const [isBusyLoading, setIsBusyLoading] = React.useState(false);
  const [isBusyUpdating, setIsBusyUpdating] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [barChartData, setBarChartData] = React.useState([] as BarChartData[]);
  const [options, setOptions] = React.useState({} as FarmSettingsOptions);
  const [farmType, setFarmType] = React.useState(FarmType.Unknown);

  const loadFarmSettingsData = async () => {
    setIsBusyLoading(true);

    settingsService
      .getFarmSettings()
      .then((settings) => {
        setBarChartData(settings.dashboardBarChartSettings || []);
        setOptions(settings.options);
        setFarmType(settings.farmType);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusyLoading(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      const loadData = async () => loadFarmSettingsData();
      loadData();
    }
  }, [loaded]);

  const updateDashboardSettings = () => {
    // validate
    if (!validatePreUpdateSettings()) {
      return;
    }

    setIsBusyUpdating(true);

    settingsService
      .updateFarmSettings(barChartData, undefined, options)
      .then(() => {
        notifications.success(t('f-settings-dash-upd-ok'));
        setLoaded(false);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusyUpdating(false);
      });
  };

  /**
   * The stored flags are "disabled" so that settings written before they existed read as enabled, but a
   * switch is easier to read as on = visible. These two helpers are the only place the two meet.
   */
  const setSectionEnabled = (key: keyof FarmSettingsOptions, enabled: boolean) => {
    setOptions((current) => ({ ...current, [key]: !enabled }));
  };

  const setBarChartEnabled = (animalType: AnimalType, enabled: boolean) => {
    setBarChartData((current) =>
      current.map((chart) => (chart.animalType === animalType ? { ...chart, isDisabled: !enabled } : chart))
    );
  };

  const validatePreUpdateSettings = (): boolean => {
    let isValid = true;

    barChartData.forEach((data) => {
      const suppliesIds = data.barChartSupplies.map((x) => x.medicalSupplyId);
      const distinctSupplies = [...new Set(suppliesIds)];

      if (distinctSupplies.length !== 3) {
        notifications.warning(t('f-val-dash-supp'));
        isValid = false;
      }
    });
    return isValid;
  };

  //#region medical supply selector logic
  const [supplyToChange, setSupplyToChange] = React.useState({} as { animalType: AnimalType; index: number });
  const medicalSupplyModalRef = React.useRef<MedicalSupplySelectModalHandle>(null!);
  const barColors = [colors.barChartA, colors.barChartB, colors.barChartC];

  const handleMedicalSupplyChange = (supply: MedicalSupplyViewModel | null) => {
    if (!supply) {
      return;
    }

    const tempChartData = [...barChartData];
    const barChartByType = tempChartData.find((x) => x.animalType === supplyToChange.animalType);

    const barSupply = barChartByType?.barChartSupplies[supplyToChange.index - 1];

    if (barSupply && barSupply.medicalSupplyId) {
      barSupply.medicalSupplyId = supply.id;
      barSupply.medicalSupply = supply.number;
    }
    setBarChartData(tempChartData);
  };

  const renderMedicalSupplySelect = (animalTypeCode: AnimalType, key: number) => {
    const barChart = barChartData.find((x) => x.animalType === animalTypeCode);
    const supply = barChart?.barChartSupplies[key - 1];

    return (
      <div className={classes.vaccineSelect}>
        <Box height={20} width={20} borderRadius={2} bgcolor={barColors[key - 1]} />

        <Typography>{supply?.medicalSupply}</Typography>

        <Tooltip arrow title={t('f-supp-change')}>
          <IconButton
            onClick={() => {
              setSupplyToChange({ animalType: animalTypeCode, index: key });
              medicalSupplyModalRef.current.openModal();
            }}
            size="small"
            style={{ color: barColors[key - 1] }}
          >
            <Icon>{'more_horiz'}</Icon>
          </IconButton>
        </Tooltip>
      </div>
    );
  };
  //#endregion medical supply selector logic

  //#region section switches
  /** One row of the sections list: what it is, what switching it off hides, and the switch itself. */
  const renderSectionToggle = (
    label: string,
    description: string,
    enabled: boolean,
    onChange: (enabled: boolean) => void
  ) => (
    <div className="flex items-center justify-between gap-4 px-4 py-3">
      <div className="min-w-0">
        <p className="m-0 text-sm font-medium text-gray-900">{label}</p>
        <p className="mt-0.5 mb-0 text-xs text-gray-500">{description}</p>
      </div>
      <Switch
        disabled={isBusyUpdating}
        checked={enabled}
        onChange={(event) => onChange(event.target.checked)}
        color="primary"
      />
    </div>
  );

  /**
   * Sections that are not medication charts. Those carry their switch on their own card instead, next to the
   * supplies they hide.
   */
  const renderSectionOptions = () => (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="divide-y divide-gray-100">
        {renderSectionToggle(
          t('f-settings-dash-piglet-reg'),
          t('f-settings-dash-piglet-reg-hint'),
          !options.pigletRegistrationWidgetDisabled,
          (enabled) => setSectionEnabled('pigletRegistrationWidgetDisabled', enabled)
        )}
      </div>
    </div>
  );
  //#endregion section switches

  const renderBarChartSettings = (animalType: AnimalType) => {
    const isEnabled = !barChartData.find((x) => x.animalType === animalType)?.isDisabled;

    return (
      <Card elevation={1}>
        <Box pt={1}>
          <div className="flex items-center justify-between gap-1 pl-4 pr-1">
            <Typography style={{ fontSize: '1.4em', fontWeight: 400 }} color={isEnabled ? 'primary' : 'textSecondary'}>
              {t(AnimalType[animalType].toString())}
            </Typography>
            <Tooltip arrow title={t('f-settings-dash-chart-toggle')}>
              <Switch
                disabled={isBusyUpdating}
                checked={isEnabled}
                onChange={(event) => setBarChartEnabled(animalType, event.target.checked)}
                color="primary"
              />
            </Tooltip>
          </div>
          <Box mt={1}>
            <Divider />
          </Box>
        </Box>
        {/* The supplies stay selected while the chart is off - re-enabling restores them - so they are only
            greyed out rather than cleared or hidden. */}
        <CardContent className={isEnabled ? undefined : 'opacity-50'}>
          {renderMedicalSupplySelect(animalType, 1)}
          {renderMedicalSupplySelect(animalType, 2)}
          {renderMedicalSupplySelect(animalType, 3)}
        </CardContent>
      </Card>
    );
  };

  const renderSuppliesSettingsCards = () => {
    const config = getDashboardConfigByFarmType(farmType);

    if (config.isPigFarm) {
      return (
        <Grid container direction="row" spacing={2}>
          {config.displaySow && (
            <Grid item xs={12} sm={6} md={3}>
              {renderBarChartSettings(AnimalType.Sow)}
            </Grid>
          )}
          {config.displayGilt && (
            <Grid item xs={12} sm={6} md={3}>
              {renderBarChartSettings(AnimalType.Gilt)}
            </Grid>
          )}
          {config.displayPiglet && (
            <Grid item xs={12} sm={6} md={3}>
              {renderBarChartSettings(AnimalType.Piglet)}
            </Grid>
          )}
          {config.displayFattening && (
            <Grid item xs={12} sm={6} md={3}>
              {renderBarChartSettings(AnimalType.Fattening)}
            </Grid>
          )}
        </Grid>
      );
    } else {
      return (
        <Grid container direction="row" spacing={1}>
          {config.displayGoat && (
            <Grid item md={3} xs={12}>
              {renderBarChartSettings(AnimalType.Goat)}
            </Grid>
          )}
          {config.displayCow && (
            <Grid item md={3} xs={12}>
              {renderBarChartSettings(AnimalType.Cow)}
            </Grid>
          )}
          {config.displayCalf && (
            <Grid item md={3} xs={12}>
              {renderBarChartSettings(AnimalType.Calf)}
            </Grid>
          )}
        </Grid>
      );
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item container xs={12} spacing={2} justifyContent="space-between" alignItems="center">
        <Grid item>
          <Box display="flex" alignItems="flex-start">
            <AssessmentIcon color="primary" fontSize="large" />
            <Box ml={1} mt={0.3}>
              <Title>{t('f-settings-dash')}</Title>
            </Box>
          </Box>
        </Grid>

        <Grid item>
          <Button
            color="primary"
            startIcon={<SaveIcon />}
            onClick={updateDashboardSettings}
            disabled={isBusyUpdating}
            variant="contained"
          >
            {t('f-com-save')}
          </Button>
        </Grid>
      </Grid>

      <Loader isLoading={isBusyLoading} size={LoaderSize.DEFAULT_SIZE} />

      {loaded && (
        <Grid item xs={12}>
          <div className="flex flex-col gap-6">
            {renderSectionOptions()}

            <div>
              <p className="m-0 text-sm font-semibold text-gray-700">{t('f-settings-dash-charts')}</p>
              <p className="mt-0.5 mb-3 text-xs text-gray-500">{t('f-settings-dash-charts-hint')}</p>
              {renderSuppliesSettingsCards()}
            </div>
          </div>
        </Grid>
      )}

      {/* button is hidden and modal itself opened by each selector click */}
      <div className={classes.displayNone}>
        <MedicalSupplySelectModal
          ref={medicalSupplyModalRef}
          disableAny
          supply={null}
          onChange={(supply) => handleMedicalSupplyChange(supply)}
        />
      </div>
    </Grid>
  );
};

export default DashboardSettings;
